// Consumer-side config for the storefront notification bars — full-width, scheduled, dismissable
// bars that sit ABOVE the header and push it (and the page) down until the shopper closes them.
// Persisted as a JSON string on IStorefrontSettings.notificationsConfig. Mirrors the admin editor
// (ac-main/web: components/Storefront/Editor/notificationsConfig.ts).
//
// Style parts reuse the generic search-config style shape (ISearchElementStyle) and are applied with
// elementStyleFrom — numbers are px, fontSize is a percent, and any camelCase CSS passes through.

import { ISearchElementStyle } from './searchPageConfig';
import { SK_NOTIFICATIONS_DISMISSED } from '../../Constants/LocalStorageKeys';

/**
 * What happens after a shopper closes the bar.
 *  * `none`       — no close button at all; the bar shows until it expires or is disabled.
 *  * `session`    — comes back on the shopper's next visit (sessionStorage).
 *  * `persistent` — stays gone on this browser (localStorage).
 */
export type NotificationDismissMode = 'none' | 'session' | 'persistent';

export interface INotificationBar {
    /** Stable identity. Doubles as the per-shopper dismissal key. */
    id: string;
    /** Admin-only label. Never rendered. */
    name?: string;
    disabled?: boolean;

    text?: string;
    linkText?: string;
    linkUrl?: string;
    newTab?: boolean;

    sortOrder?: number;

    /** ISO 8601 UTC. Absent starts immediately. */
    startsAt?: string;
    /** ISO 8601 UTC, exclusive. Absent never expires. */
    endsAt?: string;

    /** Defaults to `session`. */
    dismissMode?: NotificationDismissMode;
    /** Bumped by the editor on content changes; a dismissal recorded below this revives the bar. */
    revision?: number;

    bar?: ISearchElementStyle;
    message?: ISearchElementStyle;
    link?: ISearchElementStyle;
    closeButton?: ISearchElementStyle;
}

export interface INotificationsConfig {
    bars?: INotificationBar[];
}

export function parseNotificationsConfig(raw?: string): INotificationsConfig {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        const bars = (parsed as INotificationsConfig).bars;
        // A bar with no id has no dismissal key, so it could never be closed. Drop it.
        return { ...parsed, bars: Array.isArray(bars) ? bars.filter((b) => !!b && typeof b.id === 'string' && !!b.id) : undefined };
    } catch {
        return {};
    }
}

function instant(iso?: string): number | undefined {
    if (!iso) return undefined;
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? undefined : ms;
}

/** Is `bar` inside its scheduled window at `now`? Absent bounds are open. `endsAt` is exclusive. */
export function isScheduledNow(bar: INotificationBar, now: number): boolean {
    const from = instant(bar.startsAt);
    const to = instant(bar.endsAt);
    if (from !== undefined && now < from) return false;
    if (to !== undefined && now >= to) return false;
    return true;
}

/** Enabled + in-window bars, in display order. Dismissal is applied separately (see isDismissed). */
export function liveBars(config: INotificationsConfig, now: number): INotificationBar[] {
    return [...(config.bars ?? [])]
        .filter((b) => !b.disabled && isScheduledNow(b, now))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

// ── Per-shopper dismissal ────────────────────────────────────────────────────────────────
// Recorded as { [barId]: revisionDismissed } in the store matching the bar's own dismissMode:
// sessionStorage for `session` (back next visit), localStorage for `persistent` (gone for good).
//
// Storing the REVISION rather than a boolean is what lets a merchant reword a bar and have it reach
// the shoppers who already closed the old wording — the stored number then trails bar.revision and
// the bar renders again. Styling-only edits don't bump the revision, so a color tweak doesn't
// re-nag everyone.
//
// Reading only the store that matches the CURRENT mode is deliberate: switching a bar from
// `persistent` to `session` revives it, which is what a merchant asking for that switch means.
//
// Every access is wrapped — Safari private mode and blocked-cookie setups throw on both read and
// write, and a notification bar must never be the reason a storefront page fails to render.

function storeFor(mode: NotificationDismissMode | undefined): Storage | undefined {
    try {
        if (mode === 'persistent') return window.localStorage;
        if (mode === 'none') return undefined;
        return window.sessionStorage; // `session` and the unset default
    } catch {
        return undefined;
    }
}

function readDismissals(store?: Storage): { [id: string]: number } {
    if (!store) return {};
    try {
        const raw = store.getItem(SK_NOTIFICATIONS_DISMISSED);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
    } catch {
        return {};
    }
}

/** Has this shopper already closed this bar, at a revision at least as new as the current one? */
export function isDismissed(bar: INotificationBar): boolean {
    if (bar.dismissMode === 'none') return false;
    const at = readDismissals(storeFor(bar.dismissMode))[bar.id];
    return typeof at === 'number' && at >= (bar.revision ?? 0);
}

/** Records the dismissal at the bar's current revision. No-ops when storage is unavailable. */
export function recordDismissal(bar: INotificationBar): void {
    const store = storeFor(bar.dismissMode);
    if (!store) return;
    try {
        const all = readDismissals(store);
        all[bar.id] = bar.revision ?? 0;
        store.setItem(SK_NOTIFICATIONS_DISMISSED, JSON.stringify(all));
    } catch {
        // Storage full or blocked — the bar still closes for this page view, it just won't stay closed.
    }
}
